/**
 * ProgressRing — compact SVG donut showing a single percentage.
 *
 * The coloured arc inherits its colour from the wrapping tone class via
 * `stroke="currentColor"`, so it re-themes with the client palette.
 */

import React from 'react';
import type {AnalyticsTone} from '../models/homeAnalytics.model';
import {toneStyle} from '../utils/toneStyles';

interface ProgressRingProps {
  /** 0–100. Values outside the range are clamped. */
  percentage: number;
  tone?: AnalyticsTone;
  label?: string;
  size?: number;
}

const ProgressRing: React.FC<ProgressRingProps> = ({
  percentage,
  tone = 'primary',
  label,
  size = 96
}) => {
  const clamped = Math.min(Math.max(percentage, 0), 100);
  const stroke = 8;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (clamped / 100) * circumference;
  const center = size / 2;

  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className={`relative ${toneStyle(tone).text}`}
        style={{width: size, height: size}}
      >
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={center}
            cy={center}
            r={radius}
            strokeWidth={stroke}
            className="text-gray-100"
            stroke="currentColor"
            fill="none"
          />
          <circle
            cx={center}
            cy={center}
            r={radius}
            strokeWidth={stroke}
            stroke="currentColor"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-700"
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-lg font-bold text-grayText">
          {Math.round(clamped)}%
        </span>
      </div>
      {label && (
        <span className="text-xs text-gray-500 text-center">{label}</span>
      )}
    </div>
  );
};

export default ProgressRing;
